import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Card, CardContent, CardMedia, Button, Chip } from '@mui/material';
import { Assignment, AccessTime, EmojiEvents, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StandaloneExam } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';

const StandaloneExams: React.FC = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState<StandaloneExam[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true);
        const data = await apiService.getStandaloneExams();
        setExams(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching standalone exams:', error);
        setExams([]);
      } finally {
        setLoading(false);
      }
    };
    fetchExams();
  }, []);

  if (loading) return <LoadingSpinner message="Loading exams..." />;

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: 'Exams', path: '/exams' },
          ]}
        />

        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Standalone Exams
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Test your skills with exam-only assessments. Purchase access and take the exam when you're ready.
          </Typography>
        </Box>

        {exams.length === 0 ? (
          <Card sx={{ p: 6, borderRadius: 3, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No exams available at the moment.
            </Typography>
          </Card>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
            {exams.map((exam) => (
              <Box key={exam.id} sx={{ flex: '1 1 350px', maxWidth: '400px' }}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    },
                  }}
                  onClick={() => navigate(`/exams/${exam.hashed}`)}
                >
                  <Box sx={{ position: 'relative' }}>
                    {exam.thumbnail ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={exam.thumbnail}
                        alt={exam.title}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                          color: 'rgba(255,255,255,0.6)',
                        }}
                      >
                        <Assignment sx={{ fontSize: 80 }} />
                      </Box>
                    )}
                  </Box>
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      px: 2,
                      pt: 1.25,
                      pb: 2.25,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '36px' }}>
                      <Typography
                        variant="body1"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          lineHeight: 1.1,
                          flex: 1,
                          pr: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {exam.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          color: '#10b981',
                          fontSize: '1rem',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        ${exam.price}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        rowGap: 0.75,
                        columnGap: 0.75,
                        alignItems: 'center',
                        mt: 0.5,
                        mb: 1,
                      }}
                    >
                      {exam.duration_minutes != null && (
                        <Chip
                          icon={<AccessTime sx={{ fontSize: '1rem' }} />}
                          label={`${exam.duration_minutes} min`}
                          size="small"
                          sx={{ borderRadius: 2, height: 24 }}
                        />
                      )}
                      {exam.grants_certificate && (
                        <Chip
                          icon={<EmojiEvents sx={{ fontSize: '1rem' }} />}
                          label="Certificate"
                          size="small"
                          color="primary"
                          sx={{ borderRadius: 2, height: 24 }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default StandaloneExams;
